import JSZip from "jszip";
import axios from "axios";
import { CSV } from "@rogoag/shared-ts";
import { IAirtableAttachment } from "@domain/CRM/Attachment";

const DEFAULT_FILE_NAME = "rogo_files.zip";

export async function downloadFile(file: File, filename: string = "", zip: boolean = false) {
	await downloadFiles([file], filename, zip);
}

export async function downloadFiles(files: File[], filename: string = "", zip: boolean = true) {
	if (zip) {
		const zipFile = new JSZip();
		files.forEach((file) => {
			zipFile.file(file.name, file);
		});
		await downloadZip(zipFile, filename);
	} else {
		files.forEach((file) => {
			downloadBlob(file, file.name);
		});
	}
}

interface GroupOfFiles {
	key: string;
	files: File[];
}

export async function downloadGroupsOfFiles(files: GroupOfFiles[], filename: string = "") {
	function addFilesToZip(files: File[], folderName: string) {
		const folder = zip.folder(folderName);
		files.forEach((file) => {
			folder?.file(file.name, file);
		});
	}

	const zip = new JSZip();
	files.forEach((file) => {
		addFilesToZip(file.files, file.key);
	});
	await downloadZip(zip, filename);
}

export async function downloadAttachment(
	attachment: IAirtableAttachment,
	filename: string = "",
	zip: boolean = false,
	jobId: string = "",
) {
	return await downloadAttachments([attachment], filename, zip, false, jobId);
}

export interface DownloadResult {
	/** Filenames that could not be fetched and were omitted from the ZIP. Empty when all succeeded. */
	failedFilenames: string[];
}

export async function downloadAttachments(
	attachments: IAirtableAttachment[],
	filename: string = "",
	zip: boolean = false,
	mergeCSV: boolean = false,
	jobId: string = "",
): Promise<DownloadResult> {
	if (attachments.length === 1) {
		const att = attachments[0];
		const blob = await attachmentToBlob(att, jobId);
		const effectiveFilename = filename || att.filename;
		if (zip) {
			const zipFile = zipBlob(blob, effectiveFilename);
			await downloadZip(zipFile, effectiveFilename);
		} else {
			downloadBlob(blob, effectiveFilename);
		}
		return { failedFilenames: [] };
	} else {
		if (mergeCSV) {
			const csvResults = await Promise.all(
				attachments.map(async (f) => {
					try {
						return (await loadAttachment(f, jobId, "text")) as string;
					} catch {
						console.warn(`Failed to load CSV: ${f.filename}`);
						return null;
					}
				}),
			);
			const csvs = csvResults.filter((csv): csv is string => csv !== null && csv !== undefined);
			if (csvs.length === 0) throw new Error("All CSV downloads failed");
			const mergedCSV = CSV.merge(csvs);
			const blob = new Blob([mergedCSV], { type: "text/csv" });
			downloadBlob(blob, `${filename}.csv`);
			return { failedFilenames: [] };
		} else {
			const { blobs, failedFilenames } = await getBlobs(attachments, jobId);
			if (blobs.every((b) => b === null)) throw new Error("All file downloads failed");
			const zipFile = zipBlobs(blobs, attachments);
			await downloadZip(zipFile, filename);
			return { failedFilenames };
		}
	}
}

export async function downloadURL(url: string, filename: string = "", zip: boolean = false) {
	const blob = await getBlob(url);

	if (zip) {
		const zipFile = zipBlob(blob, filename);
		await downloadZip(zipFile, filename);
	} else {
		downloadBlob(blob, filename);
	}
}

export async function downloadZip(zip: JSZip, filename: string = "") {
	const content = await zip.generateAsync({ type: "blob" });
	if (!filename.endsWith(".zip")) filename += ".zip";
	downloadBlob(content, filename);
}

export function downloadBlob(content: Blob, filename: string = DEFAULT_FILE_NAME) {
	const u = URL.createObjectURL(content);
	const a = document.createElement("a");
	a.href = u;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(u);
}

export async function loadAttachment<T = ArrayBuffer | Blob | string>(
	attachment: IAirtableAttachment,
	recordId: string,
	responseType: FileResponseType,
) {
	return await fetchAttachment<T>(attachment, recordId, responseType);
}

/**
 * Airtable's own attachment hosts. Their URLs expire, which is the whole reason attachments
 * are fetched from the `airtableuploads` S3 mirror first — see `fetchAttachment`.
 */
const AIRTABLE_ATTACHMENT_HOSTS = ["airtable.com", "airtableusercontent.com"];

function isAirtableHosted(url: string): boolean {
	let host: string;
	try {
		host = new URL(url).hostname.toLowerCase();
	} catch {
		// Not an absolute URL — same-origin, so not Airtable's expiring CDN.
		return false;
	}
	return AIRTABLE_ATTACHMENT_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
}

/**
 * Fetch an attachment from whichever of its two sources is the durable one, falling back to
 * the other. Both sources are always tried, so nothing is unreachable either way — this only
 * decides which one is probed first.
 *
 * For an Airtable-hosted attachment the S3 mirror is the durable source (the Airtable URL
 * expires), so S3 goes first. For anything else — notably the offline sandbox, which serves
 * attachments off its own `/offline-files` mount — the URL is already durable and has no S3
 * mirror to find. Probing S3 for those was two guaranteed 404s (`<recordId>/<file>` then
 * `<file>`) before the URL that was always going to work, and DevTools logs a failed request
 * plus a console error for each one.
 */
async function fetchAttachment<T = ArrayBuffer | Blob | string>(
	attachment: IAirtableAttachment,
	recordId: string,
	responseType: FileResponseType,
): Promise<T | undefined> {
	const fromS3 = () => downloadFromS3<T>(attachment.filename, responseType, recordId);
	const fromUrl = () => loadURL<T>(attachment.url, responseType);
	const [durable, fallback] = isAirtableHosted(attachment.url) ? [fromS3, fromUrl] : [fromUrl, fromS3];

	try {
		const result = await durable();
		if (result !== undefined) return result;
	} catch {
		// Durable source failed — fall through to the other one.
	}
	return await fallback();
}

export async function loadURL<T = ArrayBuffer | Blob | string>(url: string, responseType: FileResponseType) {
	const response = await axios.get<T>(url, { responseType });
	if (response.status === 200) return response.data;
}

async function attachmentToBlob(attachment: IAirtableAttachment, recordId: string): Promise<Blob> {
	const blob = await fetchAttachment<Blob>(attachment, recordId, "blob");
	if (blob === undefined) throw new Error(`Failed to download: ${attachment.filename}`);
	return blob;
}

async function getBlobs(
	files: IAirtableAttachment[],
	recordId: string,
): Promise<{ blobs: (Blob | null)[]; failedFilenames: string[] }> {
	const results = await Promise.all(
		files.map(async (file) => {
			try {
				const blob = await attachmentToBlob(file, recordId);
				return { blob, failedFilename: null as string | null };
			} catch {
				console.warn(`Failed to download file: ${file.filename}`);
				return { blob: null as Blob | null, failedFilename: file.filename };
			}
		}),
	);
	return {
		blobs: results.map((r) => r.blob),
		failedFilenames: results.filter((r) => r.failedFilename !== null).map((r) => r.failedFilename as string),
	};
}

async function getBlob(url: string) {
	const response = await fetch(url);
	if (!response.ok) throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
	return await response.blob();
}

function zipBlobs(blobs: (Blob | null)[], files: IAirtableAttachment[]) {
	const zip = new JSZip();
	blobs.forEach((blob, i) => {
		if (blob) {
			zip.file(files[i].filename, blob);
		}
	});
	return zip;
}

function zipBlob(blob: Blob, filename: string) {
	const zip = new JSZip();
	zip.file(filename, blob);
	return zip;
}

const REGION = "us-east-2";
const BUCKET = "airtableuploads";
type FileResponseType = "arraybuffer" | "blob" | "json" | "text";

// URLs known to 404 in this session. Many Airtable attachments don't exist under their
// recordId folder, so the first probe always 404s and DevTools logs it. We cache the misses
// so repeated downloads (effect re-runs, multiple consumers of the same job) skip the dead
// path. Only 404s are cached — transient errors (CORS, offline, 5xx) still retry.
const known404 = new Set<string>();

async function downloadFromS3<T = ArrayBuffer | Blob | string>(
	filename: string,
	responseType: FileResponseType,
	recordId = "",
	bucket: string = BUCKET,
) {
	const primaryUrl = _makeS3Url(filename, recordId, bucket);
	const rootUrl = _makeS3Url(filename, "", bucket);

	if (!known404.has(primaryUrl)) {
		try {
			const response = await axios.get<T>(primaryUrl, { responseType });
			if (response.status === 200) return response.data;
		} catch (err) {
			if (axios.isAxiosError(err) && err.response?.status === 404) {
				known404.add(primaryUrl);
			}
			// fall through to root attempt
		}
	}

	if (primaryUrl === rootUrl || known404.has(rootUrl)) return undefined;
	try {
		const response2 = await axios.get<T>(rootUrl, { responseType });
		if (response2.status === 200) return response2.data;
	} catch (err) {
		if (axios.isAxiosError(err) && err.response?.status === 404) {
			known404.add(rootUrl);
		}
	}
	return undefined;
}

function _makeS3Url(filename: string, folder = "", bucket: string = BUCKET) {
	folder = folder == "" ? "" : folder.endsWith("/") ? folder : `${folder}/`;
	filename = _isEncoded(filename) ? filename : encodeURIComponent(filename);
	return `https://${bucket}.s3.${REGION}.amazonaws.com/${folder}${filename}`;
}

function _isEncoded(uri: string) {
	uri = uri || "";

	return uri !== decodeURIComponent(uri);
}
